import React, { useState } from 'react';
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const Input = ({
  label,
  type = 'text',
  error,
  success,
  helperText,
  placeholder,
  value,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  className = '',
  id,
  name,
  autoComplete,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const inputId = id || `input-${name || Math.random().toString(36).substr(2, 9)}`;
  const hasError = Boolean(error);
  const hasSuccess = Boolean(success);
  const isPassword = type === 'password';

  // Floating label logic
  const isFloating = isFocused || value;

  const baseInputClasses = `
    w-full px-4 pt-6 pb-2 bg-background border rounded-lg
    text-text transition-all duration-200 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface
    disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface
    placeholder:text-transparent
  `;

  const stateClasses = {
    default: 'border-border focus:border-primary focus:ring-primary focus:shadow-glow',
    error: 'border-red-500 focus:border-red-500 focus:ring-red-500',
    success: 'border-green-500 focus:border-green-500 focus:ring-green-500'
  };

  const getStateClass = () => {
    if (hasError) return stateClasses.error;
    if (hasSuccess) return stateClasses.success;
    return stateClasses.default;
  };

  const inputClasses = `${baseInputClasses} ${getStateClass()} ${className}`;

  const handleFocus = (e) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          id={inputId}
          name={name}
          type={isPassword ? (showPassword ? 'text' : 'password') : type}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={label}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
          className={`${inputClasses} ${isPassword ? 'pr-12' : ''}`}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${inputId}-error` :
            helperText ? `${inputId}-helper` : undefined
          }
          {...props}
        />

        {label && (
          <label 
            htmlFor={inputId}
            className={`absolute left-4 transition-all duration-200 ease-out pointer-events-none
              ${isFloating ? 'top-2 text-xs' : 'top-4 text-base'}
              ${ hasError ? 'text-red-400' :
                 hasSuccess ? 'text-green-400' :
                 isFocused ? 'text-primary' : 'text-text-subtle'
              }
            `}
          >
            {label}
            {required && !isFloating && (
              <span className="text-red-400 ml-1" aria-label="required">*</span>
            )}
          </label>
        )}

        {/* Password toggle icon */}
        {isPassword && (
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-4 flex items-center"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            tabIndex={disabled ? -1 : 0}
          >
            {showPassword ? (
              <EyeSlashIcon className="h-5 w-5 text-text-subtle hover:text-text" />
            ) : (
              <EyeIcon className="h-5 w-5 text-text-subtle hover:text-text" />
            )}
          </button>
        )}

        {/* Status Icons (Error/Success) */}
        {hasError && !isPassword && (
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        )}
        {hasSuccess && !isPassword && (
          <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
            <CheckCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Helper/Error/Success text */}
      <div className="pt-1.5 px-1">
        {hasError && (
          <p 
            id={`${inputId}-error`}
            className="text-sm text-red-400 flex items-center"
            role="alert"
          >
            <ExclamationCircleIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
            {error}
          </p>
        )}
        {hasSuccess && (
          <p className="text-sm text-green-400 flex items-center">
            <CheckCircleIcon className="h-4 w-4 mr-1.5 flex-shrink-0" />
            {success}
          </p>
        )}
        {helperText && !hasError && !hasSuccess && (
          <p 
            id={`${inputId}-helper`}
            className="text-sm text-text-subtle"
          >
            {helperText}
          </p>
        )}
      </div>
    </div>
  );
};

// Componente de textarea
export const TextArea = ({
  label,
  error,
  helperText,
  rows = 4,
  className = '',
  id,
  name,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const textareaId = id || `textarea-${name || Math.random().toString(36).substr(2, 9)}`;
  const hasError = Boolean(error);

  const baseClasses = `
    w-full px-4 py-3 text-text bg-surface border rounded-lg resize-vertical
    transition-all duration-200 ease-out
    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background
    placeholder:text-text-subtle
  `;

  const stateClass = hasError 
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
    : 'border-border focus:border-primary focus:ring-primary focus:shadow-glow';

  return (
    <div className="space-y-2">
      {label && (
        <label 
          htmlFor={textareaId}
          className={`block text-sm font-medium transition-colors duration-200 ${
            hasError ? 'text-red-400' : isFocused ? 'text-primary' : 'text-text-subtle'
          }`}
        >
          {label}
        </label>
      )}
      
      <textarea
        id={textareaId}
        name={name}
        rows={rows}
        className={`${baseClasses} ${stateClass} ${className}`}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${textareaId}-error` : undefined}
        {...props}
      />

      {hasError && (
        <p 
          id={`${textareaId}-error`}
          className="text-sm text-red-400"
          role="alert"
        >
          {error}
        </p>
      )}

      {helperText && !hasError && (
        <p className="text-sm text-text-subtle">
          {helperText}
        </p>
      )}
    </div>
  );
};

export default Input; 